<template>
  <div class="campaign-page">
    <Head title="Campaign" />

    <div class="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.14em] text-brand-500">Operations</p>
        <h1 class="mt-1 text-3xl font-bold tracking-tight text-brand-900">Campaign</h1>
        <p class="mt-1.5 text-sm text-gray-500">
          Manage campaign links, UTM mapping, and channel performance.
        </p>
      </div>
      <Link
        href="/campaign/create"
        class="inline-flex items-center justify-center rounded-xl bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700"
      >
        Create Campaign
      </Link>
    </div>

    <div class="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
      <div class="rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
        <p class="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Total campaigns</p>
        <p class="mt-1 text-xl font-bold text-brand-900">{{ formatNumber(campaigns.total || 0) }}</p>
      </div>
      <div class="rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
        <p class="text-[11px] font-semibold uppercase tracking-wide text-gray-500">On this page</p>
        <p class="mt-1 text-xl font-bold text-brand-900">{{ campaigns.data.length }}</p>
      </div>
      <div class="rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
        <p class="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Active shown</p>
        <p class="mt-1 text-xl font-bold text-brand-900">{{ activeCount }}</p>
      </div>
    </div>

    <div class="mb-5 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
      <div class="mb-4">
        <h2 class="text-sm font-bold text-brand-900">Filters</h2>
        <p class="text-xs text-gray-500">Search and filter campaigns in real time</p>
      </div>
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
        <div class="xl:col-span-2">
          <label class="mb-1.5 block text-xs font-semibold text-gray-600">Search</label>
          <input
            v-model="form.search"
            type="text"
            placeholder="Title, name, UTM ID..."
            class="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700 placeholder-gray-400 focus:border-brand-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-200"
          />
        </div>
        <div>
          <label class="mb-1.5 block text-xs font-semibold text-gray-600">Channel</label>
          <select
            v-model="form.channel"
            class="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700 focus:border-brand-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-200"
          >
            <option value="">All channels</option>
            <option v-for="opt in options" :key="opt.agtCd" :value="opt.agtCd">{{ opt.name }}</option>
          </select>
        </div>
        <div>
          <label class="mb-1.5 block text-xs font-semibold text-gray-600">Status</label>
          <select
            v-model="form.status"
            class="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700 focus:border-brand-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-200"
          >
            <option value="">All</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>
        <div>
          <label class="mb-1.5 block text-xs font-semibold text-gray-600">Sort by</label>
          <select
            v-model="form.orderBy"
            class="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700 focus:border-brand-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-200"
          >
            <option value="created_desc">Newest first</option>
            <option value="created_asc">Oldest first</option>
            <option value="title_asc">Title A–Z</option>
            <option value="title_desc">Title Z–A</option>
            <option value="status_desc">Status (Active first)</option>
            <option value="status_asc">Status (Inactive first)</option>
          </select>
        </div>
      </div>
      <div class="mt-4 flex justify-end">
        <button
          type="button"
          class="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50"
          @click="reset"
        >
          Clear filters
        </button>
      </div>
    </div>

    <div class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      <div class="border-b border-gray-100 px-5 py-3">
        <h2 class="text-sm font-bold text-brand-900">Campaign list</h2>
        <p class="text-xs text-gray-500">
          Showing {{ campaigns.data.length }} of {{ formatNumber(campaigns.total || 0) }}
        </p>
      </div>

      <div class="overflow-x-auto">
        <table class="min-w-full">
          <thead>
            <tr class="border-b border-gray-100 bg-brand-50/60 text-left">
              <th class="whitespace-nowrap px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-brand-600">Title</th>
              <th class="whitespace-nowrap px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-brand-600">Name</th>
              <th class="whitespace-nowrap px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-brand-600">UTM ID</th>
              <th class="whitespace-nowrap px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-brand-600">Channel</th>
              <th class="whitespace-nowrap px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-brand-600">Source Link</th>
              <th class="whitespace-nowrap px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-brand-600">Medium</th>
              <th class="whitespace-nowrap px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-brand-600">Status</th>
              <th class="whitespace-nowrap px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-brand-600">Count</th>
              <th class="whitespace-nowrap px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-brand-600">Created</th>
              <th class="relative px-4 py-3"><span class="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="campaign in campaigns.data"
              :key="campaign.id"
              class="border-b border-gray-50 transition-colors hover:bg-brand-50/40"
            >
              <td class="px-4 py-3.5">
                <Link
                  :href="`/campaign/${campaign.utm_id}/edit`"
                  class="text-sm font-semibold text-brand-700 hover:text-brand-900"
                >
                  {{ campaign.title }}
                </Link>
              </td>
              <td class="whitespace-nowrap px-4 py-3.5 text-sm text-gray-700">{{ campaign.name }}</td>
              <td class="whitespace-nowrap px-4 py-3.5 font-mono text-xs text-gray-600">{{ campaign.utm_id }}</td>
              <td class="whitespace-nowrap px-4 py-3.5 text-sm text-gray-700">{{ campaign.channel_id }}</td>
              <td class="px-4 py-3.5 max-w-[220px]">
                <div class="flex items-center gap-2">
                  <span class="block truncate text-sm text-gray-600" :title="normalizeSourceLink(campaign.source_link)">
                    {{ normalizeSourceLink(campaign.source_link) }}
                  </span>
                  <button
                    type="button"
                    class="shrink-0 rounded-lg p-1.5 text-gray-400 hover:bg-brand-50 hover:text-brand-700"
                    title="Copy link"
                    @click="copyText(campaign)"
                  >
                    <icon name="copy" class="h-4 w-4" />
                  </button>
                </div>
              </td>
              <td class="whitespace-nowrap px-4 py-3.5 text-sm text-gray-700">{{ campaign.medium }}</td>
              <td class="whitespace-nowrap px-4 py-3.5">
                <span
                  class="inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold"
                  :class="campaign.status === 'Active' ? 'bg-teal-50 text-teal-700' : 'bg-gray-100 text-gray-600'"
                >
                  {{ campaign.status }}
                </span>
              </td>
              <td class="whitespace-nowrap px-4 py-3.5 text-sm font-semibold text-brand-800">{{ campaign.count }}</td>
              <td class="whitespace-nowrap px-4 py-3.5 text-sm text-gray-500">{{ formatDate(campaign.created_at) }}</td>
              <td class="px-4 py-3.5 text-right">
                <Link
                  :href="`/campaign/${campaign.utm_id}/edit`"
                  class="inline-flex items-center rounded-lg p-1.5 text-gray-400 transition hover:bg-brand-50 hover:text-brand-700"
                  title="Edit"
                >
                  <icon name="cheveron-right" class="h-5 w-5" />
                </Link>
              </td>
            </tr>
            <tr v-if="!campaigns.data.length">
              <td colspan="10" class="px-4 py-16 text-center">
                <p class="text-base font-bold text-brand-900">No campaigns found</p>
                <p class="mt-1 text-sm text-gray-500">Try adjusting filters or create a new campaign.</p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="border-t border-gray-100 bg-gray-50 px-5 py-3">
        <pagination class="mt-0" :links="campaigns.links || []" preserve-scroll />
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination'
import moment from 'moment'

export default {
  components: {
    Head,
    Icon,
    Link,
    Pagination,
  },
  layout: Layout,
  props: {
    options: Array,
    filters: Object,
    campaigns: Object,
  },
  data() {
    return {
      form: {
        search: this.filters.search || '',
        channel: this.filters.channel || '',
        status: this.filters.status || '',
        orderBy: this.filters.orderBy || 'created_desc',
      },
    }
  },
  computed: {
    activeCount() {
      return (this.campaigns.data || []).filter((c) => c.status === 'Active').length
    },
  },
  watch: {
    form: {
      deep: true,
      handler: throttle(function () {
        const payload = pickBy(this.form, (v) => v !== '' && v != null)
        this.$inertia.get('/campaign', payload, { preserveState: true })
      }, 300),
    },
  },
  methods: {
    normalizeSourceLink(value) {
      const raw = String(value || '').trim()
      if (!raw) return ''
      if (/^https?:\/\//i.test(raw)) return raw

      const query = raw.startsWith('?') ? raw.slice(1) : raw
      return `https://kta.okbank.co.id/id?${query}`
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString('id-ID')
    },
    formatDate(date) {
      return date ? moment(date).format('DD/MM/YY') : '—'
    },
    reset() {
      this.form = mapValues(this.form, () => '')
      this.form.orderBy = 'created_desc'
    },
    copyText(campaign) {
      const value = this.normalizeSourceLink(campaign.source_link)
      const tempInput = document.createElement('input')
      tempInput.value = value
      document.body.appendChild(tempInput)
      tempInput.select()
      document.execCommand('copy')
      document.body.removeChild(tempInput)
    },
  },
}
</script>
